import { Head, router } from "@inertiajs/react"
import { EyeOff, ShieldBan } from "lucide-react"
import { useState } from "react"

import { Button } from "@/components/ui/button"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import AppLayout from "@/layouts/app-layout"
import SettingsLayout from "@/layouts/settings/layout"
import { unblockSettingsPrivacyPath, unhideSettingsPrivacyPath } from "@/routes"

interface HiddenContent {
  id: number
  hidden_at: string
  content: {
    id: number
    type: string
    title: string
    author?: {
      id: number
      name: string
    }
    url_path: string
  }
}

interface BlockedUser {
  id: number
  blocked_at: string
  user: {
    id: number
    name: string
    email: string
    verified: boolean
  }
}

interface Props {
  hidden_content: HiddenContent[]
  blocked_users: BlockedUser[]
}

export default function Privacy({ hidden_content, blocked_users }: Props) {
  const [processingId, setProcessingId] = useState<number | null>(null)

  const handleUnhide = (hideId: number) => {
    setProcessingId(hideId)
    router.delete(unhideSettingsPrivacyPath(), {
      data: { id: hideId },
      preserveScroll: true,
      onFinish: () => setProcessingId(null),
    })
  }

  const handleUnblock = (blockId: number) => {
    setProcessingId(blockId)
    router.delete(unblockSettingsPrivacyPath(), {
      data: { id: blockId },
      preserveScroll: true,
      onFinish: () => setProcessingId(null),
    })
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString("es-ES", {
      year: "numeric",
      month: "long",
      day: "numeric",
    })
  }

  return (
    <AppLayout>
      <SettingsLayout>
        <Head title="Privacidad - Configuración" />

        <div className="space-y-6">
          <div>
            <h1 className="text-2xl font-bold">Privacidad</h1>
            <p className="text-muted-foreground mt-1">
              Gestiona el contenido oculto y los usuarios bloqueados
            </p>
          </div>

          <Tabs defaultValue="hidden" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="hidden">
                <EyeOff className="mr-2 h-4 w-4" />
                Contenido oculto ({hidden_content.length})
              </TabsTrigger>
              <TabsTrigger value="blocked">
                <ShieldBan className="mr-2 h-4 w-4" />
                Usuarios bloqueados ({blocked_users.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="hidden" className="mt-6">
              {hidden_content.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <EyeOff className="text-muted-foreground mb-4 h-12 w-12" />
                  <p className="text-muted-foreground">
                    No has ocultado ningún contenido
                  </p>
                </div>
              ) : (
                <div className="divide-y">
                  {hidden_content.map((item) => (
                    <div
                      key={item.id}
                      className="flex items-center justify-between py-4"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium">
                          {item.content.title}
                        </p>
                        <p className="text-muted-foreground mt-1 text-xs">
                          {item.content.author && (
                            <span>Por {item.content.author.name} • </span>
                          )}
                          Oculto el {formatDate(item.hidden_at)}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleUnhide(item.id)}
                        disabled={processingId === item.id}
                        className="ml-4 shrink-0"
                      >
                        Mostrar de nuevo
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="blocked" className="mt-6">
              {blocked_users.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <ShieldBan className="text-muted-foreground mb-4 h-12 w-12" />
                  <p className="text-muted-foreground">
                    No has bloqueado a ningún usuario
                  </p>
                </div>
              ) : (
                <div className="divide-y">
                  {blocked_users.map((block) => (
                    <div
                      key={block.id}
                      className="flex items-center justify-between py-4"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="flex items-center gap-2 text-sm font-medium">
                          {block.user.name}
                          {block.user.verified && (
                            <span className="text-primary text-xs">✓</span>
                          )}
                        </p>
                        <p className="text-muted-foreground mt-1 text-xs">
                          {block.user.email} • Bloqueado el{" "}
                          {formatDate(block.blocked_at)}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleUnblock(block.id)}
                        disabled={processingId === block.id}
                        className="ml-4 shrink-0"
                      >
                        Desbloquear
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </SettingsLayout>
    </AppLayout>
  )
}
